import moment from 'moment';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {SortOrder, StudentAssignmentSortField} from 'gql/types';

import {addGenericErrorToast} from '@albert-io/atomic';
import type {
  StudentAssignment,
  StudentAssignmentsFilters,
  StudentAssignmentsQueryVariables
} from '../StudentAssignment.types';
import {useStudentAssignmentsQuery} from '../queries/StudentAssignments.queries.generated';
import type {Value as DropdownValue} from '../AssignmentStatusDropdown';

const PAGINATION_LIMIT = 25;
const TO_DO_PROXIMITY = 30;

export default function useStudentAssignments(
  classroomId: string | undefined,
  dropdownValue: DropdownValue
) {
  const [hasNextPageAfterLoadingMore, setHasNextPageAfterLoadingMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [moreAssignments, setMoreAssignments] = useState<StudentAssignment[]>([]);

  function resetMoreAssignments() {
    setMoreAssignments([]);
  }

  const limit = dropdownValue !== 'toDo' ? PAGINATION_LIMIT : undefined;

  const filters = useMemo<StudentAssignmentsFilters>(() => {
    let ret: StudentAssignmentsFilters = {
      hasStarted: {eq: true}
    };

    if (classroomId !== undefined) {
      ret.classrooms = {
        id: {eq: classroomId}
      };
    }

    if (dropdownValue === 'closed') {
      ret.isOpen = {eq: false};
    } else if (dropdownValue === 'open') {
      ret.isOpen = {eq: true};
    } else if (dropdownValue === 'toDo') {
      ret.dueDate = {
        greaterThanOrEqual: moment().subtract(TO_DO_PROXIMITY, 'days').startOf('day').format(),
        lessThanOrEqual: moment().add(TO_DO_PROXIMITY, 'days').endOf('day').format()
      };
      ret.hasSubmitted = {eq: false};
      ret.isOpen = {eq: true};
    }

    return ret;
  }, [classroomId, dropdownValue]);

  const variables = useMemo<StudentAssignmentsQueryVariables>(
    () => ({
      filters,
      limit,
      sort: [
        {
          field: StudentAssignmentSortField.DueDateProximity,
          order: SortOrder.Asc
        }
      ]
    }),
    [filters, limit]
  );

  const {data, error, fetchMore, loading} = useStudentAssignmentsQuery({variables});
  const initialAssignments = data?.currentUser?.studentAssignments.results ?? [];
  const initialHasNextPage = data?.currentUser?.studentAssignments.hasNextPage ?? false;

  const allAssignments = [...initialAssignments, ...moreAssignments];
  const hasNextPage = initialHasNextPage && hasNextPageAfterLoadingMore;

  const exposedFetchMore = useCallback(() => {
    if (!loading && !isLoadingMore && hasNextPage) {
      setIsLoadingMore(true);

      fetchMore({
        variables: {
          ...variables,
          offset: allAssignments.length
        }
      })
        .then((fetchedData) => {
          const fetchedAssignments = fetchedData.data.currentUser?.studentAssignments.results ?? [];
          const fetchedHasNextPage =
            fetchedData.data.currentUser?.studentAssignments.hasNextPage ?? false;

          setMoreAssignments((current) => [...current, ...fetchedAssignments]);
          setHasNextPageAfterLoadingMore(fetchedHasNextPage);
        })
        .catch(() => {
          addGenericErrorToast();
        })
        .finally(() => {
          setIsLoadingMore(false);
        });
    }
  }, [allAssignments.length, fetchMore, hasNextPage, isLoadingMore, loading, variables]);

  useEffect(() => {
    if (error !== undefined) {
      addGenericErrorToast();
    }
  }, [error]);

  return {
    assignments: allAssignments,
    fetchMore: exposedFetchMore,
    isLoadingMore,
    loading,
    resetMoreAssignments
  };
}
